import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    overflow: 'auto',
    borderTop: '1px solid var(--opal-border-default, #eee)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-700, #555)',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    background: 'var(--opal-surface-default, #fff)',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  thRight: { textAlign: 'right' },
  td: {
    padding: '4px 10px',
    borderBottom: '1px solid var(--opal-border-default, #f3f3f3)',
    color: 'var(--color-neutral-800, #333)'
  },
  tdRight: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  tdNegative: { color: 'var(--color-danger-600, #c62828)' },
  totalTr: {
    fontWeight: 'var(--font-weight-semibold, 600)',
    background: 'var(--theme-color-surface-d1, #f9f9f9)'
  },
  nameCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 240
  },
  dash: { color: 'var(--color-neutral-300, #ccc)' },
  // Header row labeling the skipped-rows section so the user can tell
  // they're looking at rows that DID NOT contribute to the chart.
  // Visually subdued -- it's a footnote, not the primary content.
  skippedDivider: {
    background: 'var(--theme-color-surface-d1, #f9f9f9)',
    color: 'var(--color-neutral-500, #888)',
    fontSize: 10,
    fontStyle: 'italic',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    padding: '6px 10px',
    borderTop: '1px solid var(--opal-border-default, #eee)',
    borderBottom: '1px solid var(--opal-border-default, #f3f3f3)'
  },
  skippedRow: {
    color: 'var(--color-neutral-500, #888)'
  }
}))

// Plain HTML table beneath the donut. Two sections:
//
//   1. Charted rows: one row per slice the chart rendered. Bound to
//      `slices` (already filtered + sorted by `computeSlices`).
//   2. Skipped rows (when `items` + `measureId` are provided): rows
//      from the unfiltered items array that don't appear in `slices`
//      because their value was null / undefined / NaN / Infinity or
//      exactly zero. Renders with `--` placeholders so users can see
//      what the `skippedZero` chip is counting -- the chip's number
//      always matches this section's row count.
//
// Falls back gracefully (no skipped section) when the optional
// `items` / `measureId` props aren't provided.
export default function AllocationDetailTable ({ slices, items, measureId, totalRaw, valueFormat }) {
  const classes = useStyles()
  const { formatter: fmt } = useFormattingContext()

  // Skipped rows: anything in `items` not represented in `slices`.
  // Match on the same id we use for React keys (groupId fallback to
  // name) so homonyms don't get incorrectly suppressed.
  const skippedRows = useMemo(() => {
    if (!Array.isArray(items) || !measureId) return []
    const presentKeys = new Set(slices.map(s => (s.groupId != null ? `id:${s.groupId}` : `name:${s.name}`)))
    return items.filter(item => {
      const key = item?.groupEntityId != null
        ? `id:${item.groupEntityId}`
        : item?.groupId != null
          ? `id:${item.groupId}`
          : `name:${item?.name}`
      return !presentKeys.has(key)
    })
  }, [items, measureId, slices])

  return (
    <div className={classes.wrapper}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.th}>Name</th>
            <th className={`${classes.th} ${classes.thRight}`}>Value</th>
            <th className={`${classes.th} ${classes.thRight}`}>% of Total</th>
          </tr>
        </thead>
        <tbody>
          {slices.map(slice => (
            <tr key={slice.groupId != null ? `id:${slice.groupId}` : `name:${slice.name}`}>
              <td className={classes.td}>
                <div className={classes.nameCell} title={slice.name}>{slice.name}</div>
              </td>
              <td className={`${classes.td} ${classes.tdRight} ${slice.raw < 0 ? classes.tdNegative : ''}`}>
                {fmt(slice.raw, valueFormat)}
              </td>
              <td className={`${classes.td} ${classes.tdRight}`}>
                {`${(slice.percent * 100).toFixed(2)}%`}
              </td>
            </tr>
          ))}
          {slices.length === 0 && (
            <tr>
              <td className={classes.td} colSpan={3}>
                <span className={classes.dash}>No data</span>
              </td>
            </tr>
          )}
        </tbody>
        {/*
          Skipped rows live in their own tbody so the inter-row
          divider styling is contained. Section appears only when
          there's something to surface; otherwise we don't draw the
          extra "Omitted from chart" caption.
        */}
        {skippedRows.length > 0 && (
          <tbody>
            <tr>
              <td className={classes.skippedDivider} colSpan={3}>
                Omitted from chart ({skippedRows.length})
              </td>
            </tr>
            {skippedRows.map(item => {
              const itemKey = item?.groupEntityId != null
                ? `id:${item.groupEntityId}`
                : item?.groupId != null
                  ? `id:${item.groupId}`
                  : `name:${item?.name}`
              const raw = item?.[measureId]
              const hasNumeric = typeof raw === 'number' && Number.isFinite(raw) && raw !== 0
              // Defensive: skippedRows is set-difference-derived, so
              // any row with a numeric non-zero value would imply a
              // mismatch between `computeSlices` filtering and our
              // key derivation. Render the value rather than a dash
              // so the bug is visible if it ever happens.
              return (
                <tr key={`skipped:${itemKey}`} className={classes.skippedRow}>
                  <td className={classes.td}>
                    <div className={classes.nameCell} title={item?.name}>{item?.name ?? 'Unknown'}</div>
                  </td>
                  <td className={`${classes.td} ${classes.tdRight}`}>
                    {hasNumeric ? fmt(raw, valueFormat) : <span className={classes.dash}>--</span>}
                  </td>
                  <td className={`${classes.td} ${classes.tdRight}`}>
                    <span className={classes.dash}>--</span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        )}
        {slices.length > 0 && (
          <tfoot>
            <tr className={classes.totalTr}>
              <td className={classes.td}>Total</td>
              <td className={`${classes.td} ${classes.tdRight} ${totalRaw < 0 ? classes.tdNegative : ''}`}>
                {fmt(totalRaw, valueFormat)}
              </td>
              <td className={`${classes.td} ${classes.tdRight}`}>100.00%</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

AllocationDetailTable.propTypes = {
  slices: PropTypes.arrayOf(PropTypes.shape({
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    raw: PropTypes.number.isRequired,
    abs: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  })).isRequired,
  // Optional: when present, rows from `items` not represented in
  // `slices` are listed in a separate "Omitted from chart" section.
  items: PropTypes.array,
  measureId: PropTypes.string,
  totalRaw: PropTypes.number.isRequired,
  valueFormat: PropTypes.string
}
